import { useCallback } from 'react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { Button, ButtonAppearance, Text } from '@atlassian/learning-components';
import { Flex } from '@atlaskit/primitives/compiled';
import { css } from '@compiled/react';
import WarningIcon from '@atlaskit/icon/core/warning';

import { useEnrollInSession } from 'src/api/hooks/useEnrollInSession';
import { useUnenrollFromSession } from 'src/api/hooks/useUnenrollFromSession';
import { type OtherSession, type SessionData } from 'src/types/classSession';

import { ClassTimeSlot } from './ClassTimeSlot';
import { FindOtherClasses } from './FindOtherClasses';

interface Props {
  courseId: string;
  sessionId: string;
  courseName: string;
  isEnrolled: boolean;
  enrollmentId?: string;
  isFree?: boolean;
  hasConflict: boolean;
  otherSessions: OtherSession[];
  sessionInfo: Omit<SessionData, 'id' | 'dateStart' | 'dateEnd' | 'duration' | 'instructorNames' | 'location'>;
}

export const LowerSection = ({
  courseId,
  sessionId,
  courseName,
  isEnrolled,
  enrollmentId,
  isFree,
  hasConflict,
  otherSessions,
  sessionInfo,
}: Props) => {
  const { month, day, label, time } = sessionInfo;
  const { mutateAsync: enrollInSession, isPending: isEnrollLoading } = useEnrollInSession();
  const { mutateAsync: unenrollFromSession, isPending: isUnenrollLoading } = useUnenrollFromSession();

  const onEnrollForFreeClick = useCallback(async () => {
    if (!isFree || isEnrollLoading || isEnrolled) {
      return;
    }

    await enrollInSession(
      { courseId, sessionId },
      {
        onError: (error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to enroll in session', error);
        },
      },
    );
  }, [courseId, enrollInSession, isEnrollLoading, isEnrolled, isFree, sessionId]);
  const onUnenrollClick = useCallback(async () => {
    if (!isFree || !isEnrolled || isUnenrollLoading || !enrollmentId) {
      return;
    }

    try {
      await unenrollFromSession({ courseId, enrollmentId });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to unenroll from session', error);
    }
  }, [courseId, enrollmentId, isEnrolled, isFree, isUnenrollLoading, unenrollFromSession]);
  const onEnrollPartnerClick = () => {};

  const onFreeButtonClick = isEnrolled ? onUnenrollClick : onEnrollForFreeClick;
  const onEnrollClick = isFree ? onFreeButtonClick : onEnrollPartnerClick;
  const buttonLabel = isFree
    ? isEnrolled
      ? 'Unenroll'
      : 'Enroll for free'
    : 'Enroll on a partner site';

  return (
    <div css={lowerSectionContainerStyles}>
      <div css={slotActionsContainerStyles}>
        <ClassTimeSlot iconMonth={month} iconDay={day} titleDate={label} subtitleTime={time} />
        <div css={buttonContainerStyles}>
          <FindOtherClasses courseName={courseName} sessions={otherSessions} />
          <Button
            label={buttonLabel}
            appearance={ButtonAppearance.PRIMARY}
            onClick={onEnrollClick}
            isLoading={Boolean(
              isFree && (isEnrolled ? isUnenrollLoading : isEnrollLoading),
            )}
          />
        </div>
      </div>
      {hasConflict && (
        <Flex alignItems="center" gap="space.050">
          <WarningIcon label="warning" color={token('color.icon.warning')} />
          <Text>You’re enrolled in another class that conflicts with this time</Text>
        </Flex>
      )}
    </div>
  );
};

const slotActionsContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: token('space.400'),

  [media.above.sm]: {
    flexDirection: 'row',
    gap: token('space.400'),
  },
});

const buttonContainerStyles = css({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.200'),
  alignItems: 'center',
});

const lowerSectionContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.400'),

  [media.above.sm]: {
    gap: token('space.200'),
  },
});
